import React from 'react'
import { createPortal } from 'react-dom'
import { CalendarIcon, CheckCircleIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Text, LoadingSpinner } from '../ui'
import { EmailChipsInput, type EmailChipsInputHandle } from './EmailChipsInput'
import { ContactPhoneInput } from './ContactPhoneInput'
import {
  AppointmentCreatedSummary,
  AppointmentFormField,
  AppointmentFormSection,
  appointmentFormInputClass,
} from './appointmentFormUi'
import {
  AgendaAvailabilityModal,
  type AgendaAvailabilitySelection,
} from './AgendaAvailabilityModal'
import {
  NO_ASSISTANCE_MESSAGE,
  RELATIONSHIP_TO_HOLDER_OPTIONS,
  appointmentToForm,
  buildDeceasedEligibilityCatalog,
  combineDateAndHour,
  datePart,
  formatCpf,
  fromDatetimeLocal,
  hourPart,
  isMinorFromIsoDate,
  isoDateToEdgeBirthdate,
  loadContactsPrefill,
  onlyDigits,
  pickCoverageForAppointment,
  preserveScheduledAt,
  toDatetimeLocal,
  uniqueEligibleCoverages,
  uniqueEligibleCoveragesForSpecialistScope,
} from './appointmentFormHelpers'
import { supportCenterAppointmentsService } from '../../services/supportCenterAppointments'
import { supportCenterAssistancesService } from '../../services/supportCenterAssistances'
import {
  checkSlotBookable,
  getSchedulingDateBounds,
  isSlotBookable,
  slotBookableErrorMessage,
  supportCenterParametersService,
} from '../../services/supportCenterParameters'
import type { SupportCenterSpecialization } from '../../types/supportCenterSpecialties'
import type {
  SupportCenterAgendaEvent,
  SupportCenterAttendantOption,
  SupportCenterAppointmentContactOwner,
  SupportCenterAppointmentCreateInput,
  SupportCenterAppointmentFormChannel,
} from '../../types/supportCenterAppointments'
import {
  EMPTY_APPOINTMENT_FORM,
  SUPPORT_CENTER_APPOINTMENT_CANCEL_CHANNEL_OPTIONS,
  SUPPORT_CENTER_APPOINTMENT_FORM_CHANNEL_OPTIONS,
  appointmentChannelLabel,
  type SupportCenterAppointmentCancelChannel,
  type SupportCenterAppointmentFormState,
  type SupportCenterHolderVitalStatus,
} from '../../types/supportCenterAppointments'
import { formatDateTime } from './dashboardHelpers'
import type { SupportCenterCoverageScope } from '../../types/supportCenterAssistances'
import {
  DEFAULT_SCHEDULING_LIMITS,
  type SupportCenterSchedulingLimits,
} from '../../types/supportCenterParameters'
import type { SupportCenterElegibilidadeProfissional } from '../../types/supportCenterAgenda'
import { supportCenterCancelReasonsService } from '../../services/supportCenterCancelReasons'
import type { SupportCenterCancelReason } from '../../types/supportCenterCancelReasons'

type SummaryRow = { label: string; value: string; wide?: boolean; href?: string }

type CreatedAppointmentSummary = {
  rows: SummaryRow[]
  /** Preenchido no reagendamento: dados da consulta que foi cancelada. */
  cancelledRows?: SummaryRow[]
  /** Pré salvo (hold) vs agendamento confirmado via API. */
  kind?: 'appointment' | 'prebooking'
}

function buildAppointmentSummaryRows(input: {
  scheduledAt: string
  channel: string | null | undefined
  assistanceLabel: string
  specializationName: string
  professionalName: string
  holderName: string
  holderCpf: string
  patientName: string
  patientCpf: string
  patientIsHolder: boolean
  relationship: string
  patientPhone?: string
  patientEmail?: string
  meetingLink?: string
}): SummaryRow[] {
  const rows: SummaryRow[] = [
    { label: 'Data e horário', value: formatDateTime(input.scheduledAt), wide: true },
    { label: 'Canal', value: appointmentChannelLabel(input.channel) },
    { label: 'Assistência', value: input.assistanceLabel },
    { label: 'Especialidade', value: input.specializationName },
    { label: 'Profissional', value: input.professionalName },
    { label: 'Segurado', value: input.holderName },
    { label: 'CPF do segurado', value: formatCpf(input.holderCpf) },
    {
      label: 'Paciente',
      value: input.patientIsHolder ? input.holderName : input.patientName,
    },
    { label: 'CPF do paciente', value: formatCpf(input.patientCpf) },
  ]
  if (!input.patientIsHolder && input.relationship.trim()) {
    rows.push({ label: 'Parentesco', value: input.relationship.trim() })
  }
  const patientPhone = input.patientPhone?.trim() || ''
  const patientEmail = input.patientEmail?.trim() || ''
  if (patientPhone) {
    rows.push({ label: 'WhatsApp do paciente', value: patientPhone })
  }
  if (patientEmail) {
    rows.push({ label: 'E-mail do paciente', value: patientEmail, wide: true })
  }
  const meetingLink = input.meetingLink?.trim() || ''
  if (meetingLink) {
    rows.push({ label: 'Link da reunião', value: meetingLink, wide: true, href: meetingLink })
  }
  return rows
}

type AppointmentFormModalProps = {
  open: boolean
  mode: 'create' | 'edit'
  initial?: SupportCenterAgendaEvent | null
  initialScheduledAt?: string
  specializations: SupportCenterSpecialization[]
  /**
   * Vínculos do usuário em support_center_specialty_users.
   * Sem "ver valores": filtrar especialidades e assistências do profissional.
   * Com "ver valores": passar [] (só elegibilidade do CPF).
   */
  userSpecializationIds?: string[]
  /**
   * Sem "ver valores": só marca para o próprio usuário e restringe
   * especialidades/assistências aos vínculos dele (mesmo com lista vazia).
   */
  restrictToOwnProfessional?: boolean
  currentUserId: string | null
  canSubmit: boolean
  onClose: () => void
  onSaved: (event: SupportCenterAgendaEvent) => void
}

export const AppointmentFormModal: React.FC<AppointmentFormModalProps> = ({
  open,
  mode,
  initial,
  initialScheduledAt,
  specializations,
  userSpecializationIds = [],
  restrictToOwnProfessional = false,
  currentUserId,
  canSubmit,
  onClose,
  onSaved,
}) => {
  const [form, setForm] = React.useState<SupportCenterAppointmentFormState>(EMPTY_APPOINTMENT_FORM)
  const [createdSummary, setCreatedSummary] = React.useState<CreatedAppointmentSummary | null>(null)
  const [saving, setSaving] = React.useState(false)
  const [savingPreBooking, setSavingPreBooking] = React.useState(false)
  /** Id do pré já gravado nesta sessão (ou ao abrir um pré existente para confirmar). */
  const [preBookingId, setPreBookingId] = React.useState<string | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [warning, setWarning] = React.useState<string | null>(null)
  const [cancelReasonId, setCancelReasonId] = React.useState('')
  const [cancellationChannel, setCancellationChannel] = React.useState<
    '' | SupportCenterAppointmentCancelChannel
  >('')
  const [cancelReasons, setCancelReasons] = React.useState<SupportCenterCancelReason[]>([])
  const [loadingCancelReasons, setLoadingCancelReasons] = React.useState(false)
  const [lookingUp, setLookingUp] = React.useState<'holder' | 'patient' | null>(null)
  /** Edge não encontrou o dependente: nome deve ser digitado no formulário. */
  const [patientNameManual, setPatientNameManual] = React.useState(false)
  const [contactEmailSuggestions, setContactEmailSuggestions] = React.useState<string[]>([])
  const [contactPhoneSuggestions, setContactPhoneSuggestions] = React.useState<string[]>([])
  const [professionals, setProfessionals] = React.useState<SupportCenterAttendantOption[]>([])
  const [availableHours, setAvailableHours] = React.useState<
    Array<{ horario: string; scheduled_at: string }>
  >([])
  const [loadingSlots, setLoadingSlots] = React.useState(false)
  const [slotsError, setSlotsError] = React.useState<string | null>(null)
  const [availabilityModalOpen, setAvailabilityModalOpen] = React.useState(false)
  /** Profissionais livres no horário travado (null = filtro ainda não aplicado / sem lock). */
  const [freeProfessionalIds, setFreeProfessionalIds] = React.useState<Set<string> | null>(null)
  const [loadingSlotFilter, setLoadingSlotFilter] = React.useState(false)
  const [slotFilterWarning, setSlotFilterWarning] = React.useState<string | null>(null)
  const slotAvailabilityCacheRef = React.useRef<
    Map<string, Array<{ horario: string; scheduled_at: string }>>
  >(new Map())
  const clientEmailsInputRef = React.useRef<EmailChipsInputHandle>(null)
  /** null = ainda não validou elegibilidade; [] = sem match / erro */
  const [eligibleSpecializationIds, setEligibleSpecializationIds] = React.useState<string[] | null>(
    null
  )
  /** Catálogo + pessoas vindos de GET /elegibilidade (uma única request). */
  const [eligibleEspecializacoes, setEligibleEspecializacoes] = React.useState<
    SupportCenterElegibilidadeProfissional[] | null
  >(null)
  const [schedulingLimits, setSchedulingLimits] =
    React.useState<SupportCenterSchedulingLimits>(DEFAULT_SCHEDULING_LIMITS)
  /** Especialidades do catálogo vinculadas à assistência selecionada (null = ainda não carregou). */
  const [catalogSpecIdsForAssistance, setCatalogSpecIdsForAssistance] = React.useState<
    string[] | null
  >(null)
  /** Cobertura da assistência do catálogo (passo 2). */
  const [selectedCoverageScope, setSelectedCoverageScope] =
    React.useState<SupportCenterCoverageScope | null>(null)
  const step2FirstControlRef = React.useRef<HTMLButtonElement>(null)
  const pendingStep2FocusRef = React.useRef(false)
  /** Chave `cpf|status` já consultada. Evita segunda busca no Tab/blur. */
  const lookedUpHolderCpfRef = React.useRef<string | null>(null)
  const lookedUpPatientCpfRef = React.useRef<string | null>(null)
  const holderStatusRef = React.useRef<SupportCenterHolderVitalStatus>(form.holder_status)
  holderStatusRef.current = form.holder_status

  React.useEffect(() => {
    if (lookingUp === 'holder' || !pendingStep2FocusRef.current) return
    pendingStep2FocusRef.current = false
    step2FirstControlRef.current?.focus()
  }, [lookingUp])

  const applyContactPrefill = React.useCallback((taxId: string, forceReplace = false) => {
    if (onlyDigits(taxId).length !== 11) {
      setContactPhoneSuggestions([])
      setContactEmailSuggestions([])
      return
    }
    void loadContactsPrefill(taxId)
      .then(({ phones, emails }) => {
        setContactPhoneSuggestions(phones)
        setContactEmailSuggestions(emails)
        setForm((prev) => ({
          ...prev,
          patient_phone:
            forceReplace || !prev.patient_phone.trim()
              ? phones[0]
                ? phones[0]
                : forceReplace
                  ? ''
                  : prev.patient_phone
              : prev.patient_phone,
          client_emails:
            forceReplace || prev.client_emails.length === 0
              ? emails.length > 0
                ? [emails[0]]
                : forceReplace
                  ? []
                  : prev.client_emails
              : prev.client_emails,
        }))
      })
      .catch(() => {
        setContactPhoneSuggestions([])
        setContactEmailSuggestions([])
      })
  }, [])

  const selectedDate = datePart(form.scheduled_at)
  const selectedHour = hourPart(form.scheduled_at)
  /** Completar pré existente: manter profissional/horário do hold (como no reagendar). */
  const confirmingPreBooking =
    mode === 'create' &&
    (Boolean(preBookingId) || initial?.status === 'pre_agendado')
  /** Clique na grade só pré-preenche; data e horário permanecem editáveis. */
  const slotLocked = false
  const dateBounds = React.useMemo(
    () => getSchedulingDateBounds(schedulingLimits),
    [schedulingLimits]
  )
  const selectedProfessionalEmail =
    professionals.find((p) => p.id === form.professional_user_id)?.email?.trim() || ''

  /** Sem ver valores: sempre restringe às especialidades do profissional (mesmo sem vínculos). */
  const isSpecialistScope = restrictToOwnProfessional || userSpecializationIds.length > 0
  /** Sem ver valores OU profissional com vínculos: profissional = usuário logado. */
  const lockProfessionalToSelf = restrictToOwnProfessional || userSpecializationIds.length > 0
  const userSpecSet = React.useMemo(
    () => new Set(userSpecializationIds),
    [userSpecializationIds]
  )
  /** Códigos de assistência do catálogo nas especialidades do profissional. */
  const allowedAssistanceCodes = React.useMemo(() => {
    if (!isSpecialistScope) return null
    const codes = new Set<string>()
    for (const spec of specializations) {
      if (!userSpecSet.has(spec.id)) continue
      for (const assistance of spec.assistances ?? []) {
        const code = assistance.code.trim()
        if (code) codes.add(code)
      }
    }
    return codes
  }, [isSpecialistScope, specializations, userSpecSet])

  const resolveScopedCoverages = React.useCallback(
    (profissionais: SupportCenterElegibilidadeProfissional[] | null | undefined) => {
      if (!isSpecialistScope) return uniqueEligibleCoverages(profissionais)
      return uniqueEligibleCoveragesForSpecialistScope(
        profissionais,
        userSpecSet,
        allowedAssistanceCodes ?? undefined
      )
    },
    [isSpecialistScope, userSpecSet, allowedAssistanceCodes]
  )

  const selectableSpecializations = React.useMemo(() => {
    if (!eligibleSpecializationIds) return []
    const eligible = new Set(eligibleSpecializationIds)
    const assistanceCode = form.assistance_code.trim()
    if (!assistanceCode) return []
    if (catalogSpecIdsForAssistance === null) return []
    const catalogSet = new Set(catalogSpecIdsForAssistance)
    return specializations.filter((spec) => {
      if (!eligible.has(spec.id)) return false
      if (isSpecialistScope && !userSpecSet.has(spec.id)) return false
      if (!catalogSet.has(spec.id)) return false
      const elegSpec = eligibleEspecializacoes?.find((item) => item.id === spec.id)
      if (form.holder_status === 'ativo') {
        const hasCoverage = (elegSpec?.coberturas ?? []).some(
          (cobertura) => cobertura.code.trim() === assistanceCode
        )
        if (!hasCoverage) return false
        if (slotLocked && freeProfessionalIds) {
          const pros = elegSpec?.profissionais ?? []
          if (!pros.some((p) => freeProfessionalIds.has(p.id))) return false
        }
      }
      // Falecido: especialidades pelo vínculo do catálogo (catalogSpecIdsForAssistance).
      return true
    })
  }, [
    eligibleSpecializationIds,
    specializations,
    isSpecialistScope,
    userSpecSet,
    slotLocked,
    freeProfessionalIds,
    eligibleEspecializacoes,
    form.assistance_code,
    form.holder_status,
    catalogSpecIdsForAssistance,
  ])

  const eligibilityReady = eligibleSpecializationIds !== null
  const hasEligibleSpecs = selectableSpecializations.length > 0
  /** Só permite criar/reagendar após elegibilidade OK com ao menos um profissional no escopo. */
  const canSchedule = hasEligibleSpecs

  /** Passos 1 + 2 + 4 — habilita "Criar pré-agendamento" (passo 3 opcional). */
  const canCreatePreBooking = React.useMemo(() => {
    if (mode !== 'create' || !canSubmit || saving || savingPreBooking || lookingUp !== null) {
      return false
    }
    if (!canSchedule) return false
    const holderCpf = onlyDigits(form.holder_cpf)
    if (holderCpf.length !== 11) return false
    if (!form.holder_name.trim() || !form.holder_birth_date.trim()) return false
    if (!form.assistance_code.trim()) return false
    const channel = form.channel
    if (channel !== 'whatsapp' && channel !== 'telefone' && channel !== 'videochamada') {
      return false
    }
    if (!form.specialization_id || !form.professional_user_id) return false
    if (!selectedDate || !selectedHour) return false
    if (!form.scheduled_at || form.scheduled_at.length < 16) return false
    return true
  }, [
    mode,
    canSubmit,
    saving,
    savingPreBooking,
    lookingUp,
    canSchedule,
    form.holder_cpf,
    form.holder_name,
    form.holder_birth_date,
    form.assistance_code,
    form.channel,
    form.specialization_id,
    form.professional_user_id,
    form.scheduled_at,
    selectedDate,
    selectedHour,
  ])

  /**
   * Tudo exigido pelo POST /agendamentos (passos 1–4), espelhando handleSubmit.
   * Habilita "Criar agendamento" / "Reagendar".
   */
  const canCreateAppointment = React.useMemo(() => {
    if (!canSubmit || saving || savingPreBooking || lookingUp !== null) return false
    if (!canSchedule) return false

    const holderCpf = onlyDigits(form.holder_cpf)
    if (holderCpf.length !== 11) return false
    if (!form.holder_name.trim() || !form.holder_birth_date.trim()) return false
    if (!form.assistance_code.trim()) return false

    const channel = form.channel
    if (channel !== 'whatsapp' && channel !== 'telefone' && channel !== 'videochamada') {
      return false
    }

    const patientCpf = form.patient_is_insured ? holderCpf : onlyDigits(form.patient_cpf)
    if (patientCpf.length !== 11) return false
    if (!form.patient_is_insured) {
      if (!form.patient_name.trim() || !form.patient_birth_date.trim()) return false
      const relationship = form.relationship_to_holder.trim()
      if (
        !(RELATIONSHIP_TO_HOLDER_OPTIONS as readonly string[]).includes(relationship)
      ) {
        return false
      }
    }
    if (selectedCoverageScope === 'titular' && !form.patient_is_insured) return false

    if (!form.specialization_id || !form.professional_user_id) return false
    if (!selectedDate || !selectedHour) return false
    if (!form.scheduled_at || form.scheduled_at.length < 16) return false

    const patientPhone = form.patient_phone.trim()
    if (!patientPhone || onlyDigits(patientPhone).length < 10) return false
    if (form.client_emails.every((email) => !email.trim())) return false

    const professionalEmail =
      professionals.find((p) => p.id === form.professional_user_id)?.email?.trim() || ''
    if (!professionalEmail) return false

    if (form.holder_status === 'ativo') {
      const cobertura = pickCoverageForAppointment(
        eligibleEspecializacoes,
        form.specialization_id,
        form.assistance_code
      )
      if (
        cobertura?.codApolice == null ||
        cobertura?.codConvenio == null ||
        cobertura?.codCorretor == null ||
        cobertura?.codCliente == null
      ) {
        return false
      }
    }

    if (mode === 'edit') {
      if (!cancelReasonId.trim()) return false
      if (
        cancellationChannel !== 'whatsapp' &&
        cancellationChannel !== 'telefone' &&
        cancellationChannel !== 'videochamada'
      ) {
        return false
      }
    }

    return true
  }, [
    canSubmit,
    saving,
    savingPreBooking,
    lookingUp,
    canSchedule,
    form.holder_cpf,
    form.holder_name,
    form.holder_birth_date,
    form.holder_status,
    form.assistance_code,
    form.channel,
    form.patient_is_insured,
    form.patient_cpf,
    form.patient_name,
    form.patient_birth_date,
    form.relationship_to_holder,
    form.specialization_id,
    form.professional_user_id,
    form.scheduled_at,
    form.patient_phone,
    form.client_emails,
    selectedCoverageScope,
    selectedDate,
    selectedHour,
    professionals,
    eligibleEspecializacoes,
    mode,
    cancelReasonId,
    cancellationChannel,
  ])

  const submitDisabled = !canCreateAppointment

  const holderCoverages = React.useMemo(
    () => resolveScopedCoverages(eligibleEspecializacoes),
    [eligibleEspecializacoes, resolveScopedCoverages]
  )
  const showAssistanceStep =
    lookingUp !== 'holder' &&
    eligibleEspecializacoes !== null &&
    Boolean(form.holder_name.trim() || eligibleEspecializacoes.length > 0)

  const applyHolderEligibilitySuccess = React.useCallback(
    (
      digits: string,
      pessoa: { name: string | null; captured_birth_date: string | null },
      profissionais: SupportCenterElegibilidadeProfissional[]
    ) => {
      const profissionalIds = profissionais.map((spec) => spec.id)
      setEligibleSpecializationIds(profissionalIds)
      setEligibleEspecializacoes(profissionais)

      const scopedIds = specializations
        .filter((spec) => {
          if (!profissionalIds.includes(spec.id)) return false
          if (isSpecialistScope && !userSpecSet.has(spec.id)) return false
          return true
        })
        .map((s) => s.id)
      const scopedSet = new Set(scopedIds)

      setForm((prev) => {
        const name = pessoa.name?.trim() || ''
        const birth = pessoa.captured_birth_date?.slice(0, 10) ?? ''
        const coverages = resolveScopedCoverages(profissionais)
        const keepAssistance = Boolean(
          prev.assistance_code &&
            coverages.some((cobertura) => cobertura.code.trim() === prev.assistance_code.trim())
        )
        const autoAssistance =
          !keepAssistance && coverages.length === 1
            ? coverages[0].code
            : keepAssistance
              ? prev.assistance_code
              : ''
        const keepSpec = Boolean(
          autoAssistance &&
            prev.specialization_id &&
            scopedSet.has(prev.specialization_id) &&
            (profissionais.find((item) => item.id === prev.specialization_id)?.coberturas ?? []).some(
              (cobertura) => cobertura.code.trim() === autoAssistance.trim()
            )
        )
        // Completar pré: não apagar spec/profissional/hora já hidratados do hold.
        const keepPreBookingSlot =
          mode === 'create' &&
          initial?.status === 'pre_agendado' &&
          Boolean(prev.specialization_id && prev.professional_user_id && hourPart(prev.scheduled_at))
        const retainSpec = keepSpec || keepPreBookingSlot
        return {
          ...prev,
          holder_cpf: formatCpf(digits),
          holder_name: name,
          holder_birth_date: birth,
          ...(prev.patient_is_insured
            ? {
                patient_cpf: formatCpf(digits),
                patient_name: name,
                patient_birth_date: birth,
                contact_owner: 'holder' as SupportCenterAppointmentContactOwner,
              }
            : {}),
          assistance_code: autoAssistance || (keepPreBookingSlot ? prev.assistance_code : ''),
          specialization_id: retainSpec ? prev.specialization_id : '',
          professional_user_id: retainSpec
            ? lockProfessionalToSelf
              ? currentUserId ?? ''
              : prev.professional_user_id
            : '',
          scheduled_at:
            slotLocked && hourPart(prev.scheduled_at)
              ? prev.scheduled_at
              : retainSpec
                ? prev.scheduled_at
                : datePart(prev.scheduled_at)
                  ? `${datePart(prev.scheduled_at)}T`
                  : '',
        }
      })

      void loadContactsPrefill(digits)
        .then(({ phones, emails }) => {
          setForm((prev) => {
            if (!prev.patient_is_insured) return prev
            void Promise.resolve().then(() => {
              setContactPhoneSuggestions(phones)
              setContactEmailSuggestions(emails)
            })
            return {
              ...prev,
              patient_phone: prev.patient_phone.trim()
                ? prev.patient_phone
                : phones[0] || prev.patient_phone,
              client_emails:
                prev.client_emails.length > 0
                  ? prev.client_emails
                  : emails.length > 0
                    ? [emails[0]]
                    : prev.client_emails,
            }
          })
        })
        .catch(() => {
          /* prefill opcional */
        })
    },
    [
      specializations,
      currentUserId,
      isSpecialistScope,
      userSpecSet,
      slotLocked,
      lockProfessionalToSelf,
      resolveScopedCoverages,
      mode,
      initial,
    ]
  )

  const clearHolderEligibility = React.useCallback(
    (digits?: string) => {
      setEligibleSpecializationIds([])
      setEligibleEspecializacoes([])
      setProfessionals([])
      setContactPhoneSuggestions([])
      setContactEmailSuggestions([])
      setForm((prev) => ({
        ...prev,
        holder_name: '',
        holder_birth_date: '',
        assistance_code: '',
        specialization_id: '',
        professional_user_id: '',
        client_emails: [],
        patient_phone: '',
        scheduled_at: preserveScheduledAt(prev.scheduled_at, slotLocked),
        ...(prev.patient_is_insured
          ? {
              patient_name: '',
              patient_birth_date: '',
              ...(digits ? { patient_cpf: formatCpf(digits) } : {}),
            }
          : {}),
      }))
    },
    [slotLocked]
  )

  const applyHolderDeceasedSuccess = React.useCallback(
    async (
      digits: string,
      pessoa: { name: string | null; captured_birth_date: string | null }
    ) => {
      const assistances = await supportCenterAssistancesService.list()
      const catalog = buildDeceasedEligibilityCatalog({
        specializations,
        assistances,
        userSpecSet,
        isSpecialistScope,
      })
      setEligibleSpecializationIds(catalog.especialidadeIds)
      setEligibleEspecializacoes(catalog.especialidades)

      const coverages = resolveScopedCoverages(catalog.especialidades)
      setForm((prev) => {
        const name = pessoa.name?.trim() || ''
        const birth = pessoa.captured_birth_date?.slice(0, 10) ?? ''
        const keepAssistance = Boolean(
          prev.assistance_code &&
            coverages.some((cobertura) => cobertura.code.trim() === prev.assistance_code.trim())
        )
        const autoAssistance =
          !keepAssistance && coverages.length === 1
            ? coverages[0].code
            : keepAssistance
              ? prev.assistance_code
              : ''
        return {
          ...prev,
          holder_cpf: formatCpf(digits),
          holder_name: name,
          holder_birth_date: birth,
          ...(prev.patient_is_insured
            ? {
                patient_cpf: formatCpf(digits),
                patient_name: name,
                patient_birth_date: birth,
                contact_owner: 'holder' as SupportCenterAppointmentContactOwner,
              }
            : {}),
          assistance_code: autoAssistance,
          specialization_id: autoAssistance && keepAssistance ? prev.specialization_id : '',
          professional_user_id:
            autoAssistance && keepAssistance
              ? lockProfessionalToSelf
                ? currentUserId ?? ''
                : prev.professional_user_id
              : '',
          scheduled_at:
            slotLocked && hourPart(prev.scheduled_at)
              ? prev.scheduled_at
              : autoAssistance && keepAssistance
                ? prev.scheduled_at
                : datePart(prev.scheduled_at)
                  ? `${datePart(prev.scheduled_at)}T`
                  : '',
        }
      })

      void loadContactsPrefill(digits)
        .then(({ phones, emails }) => {
          setForm((prev) => {
            if (!prev.patient_is_insured) return prev
            void Promise.resolve().then(() => {
              setContactPhoneSuggestions(phones)
              setContactEmailSuggestions(emails)
            })
            return {
              ...prev,
              patient_phone: prev.patient_phone.trim()
                ? prev.patient_phone
                : phones[0] || prev.patient_phone,
              client_emails:
                prev.client_emails.length > 0
                  ? prev.client_emails
                  : emails.length > 0
                    ? [emails[0]]
                    : prev.client_emails,
            }
          })
        })
        .catch(() => {
          /* prefill opcional */
        })

      if (catalog.especialidadeIds.length === 0 || coverages.length === 0) {
        setError(NO_ASSISTANCE_MESSAGE)
      }
    },
    [
      specializations,
      userSpecSet,
      isSpecialistScope,
      resolveScopedCoverages,
      lockProfessionalToSelf,
      currentUserId,
      slotLocked,
    ]
  )

  const lookupHolderEligibility = React.useCallback(
    async (rawCpf: string, statusOverride?: SupportCenterHolderVitalStatus) => {
      const digits = onlyDigits(rawCpf).slice(0, 11)
      if (digits.length !== 11) return
      const status = statusOverride ?? holderStatusRef.current
      const lookupKey = `${digits}|${status}`
      if (lookedUpHolderCpfRef.current === lookupKey) return
      lookedUpHolderCpfRef.current = lookupKey
      setLookingUp('holder')
      setError(null)
      try {
        if (status === 'falecido') {
          const pessoa = await supportCenterAppointmentsService.getPessoaCpf(digits)
          if (lookedUpHolderCpfRef.current !== lookupKey) return
          await applyHolderDeceasedSuccess(digits, pessoa)
          return
        }

        const [pessoaResult, elegResult] = await Promise.allSettled([
          supportCenterAppointmentsService.getPessoaCpf(digits),
          supportCenterAppointmentsService.getElegibilidade(digits),
        ])
        if (lookedUpHolderCpfRef.current !== lookupKey) return

        if (elegResult.status === 'rejected') {
          lookedUpHolderCpfRef.current = null
          clearHolderEligibility(digits)
          const reason = elegResult.reason
          const message =
            reason instanceof Error && reason.message.trim()
              ? reason.message.trim()
              : 'Não foi possível validar elegibilidade do CPF.'
          setError(message)
          return
        }

        const result = elegResult.value
        const pessoa =
          pessoaResult.status === 'fulfilled'
            ? pessoaResult.value
            : { name: null, tax_id_number: digits, captured_birth_date: null }

        applyHolderEligibilitySuccess(digits, pessoa, result.especialidades)
        const scopedCount = specializations.filter((spec) => {
          if (!result.especialidadeIds.includes(spec.id)) return false
          if (isSpecialistScope && !userSpecSet.has(spec.id)) return false
          return true
        }).length
        if (scopedCount === 0) {
          setError(NO_ASSISTANCE_MESSAGE)
        }
      } catch (e) {
        if (lookedUpHolderCpfRef.current !== lookupKey) return
        lookedUpHolderCpfRef.current = null
        clearHolderEligibility(digits)
        const message =
          e instanceof Error && e.message.trim()
            ? e.message.trim()
            : status === 'falecido'
              ? 'Não foi possível buscar o CPF do segurado.'
              : 'Não foi possível validar elegibilidade do CPF.'
        setError(message)
      } finally {
        if (
          lookedUpHolderCpfRef.current === lookupKey ||
          lookedUpHolderCpfRef.current === null
        ) {
          setLookingUp(null)
        }
      }
    },
    [
      applyHolderDeceasedSuccess,
      applyHolderEligibilitySuccess,
      clearHolderEligibility,
      specializations,
      isSpecialistScope,
      userSpecSet,
    ]
  )

  const lookupPatientPessoa = React.useCallback(async (rawCpf: string, birthDateYmd?: string) => {
    const digits = onlyDigits(rawCpf).slice(0, 11)
    if (digits.length !== 11) return
    const birthKey = (birthDateYmd ?? '').trim().slice(0, 10)
    const lookupKey = `${digits}|${birthKey}`
    if (lookedUpPatientCpfRef.current === lookupKey) return
    lookedUpPatientCpfRef.current = lookupKey
    setLookingUp('patient')
    setError(null)
    setPatientNameManual(false)
    const edgeBirthdate =
      birthKey && isMinorFromIsoDate(birthKey) ? isoDateToEdgeBirthdate(birthKey) : null
    try {
      const person = await supportCenterAppointmentsService.getPessoaCpf(digits, edgeBirthdate)
      if (lookedUpPatientCpfRef.current !== lookupKey) return
      const resolvedName = person.name?.trim() || ''
      setPatientNameManual(!resolvedName)
      setForm((prev) => ({
        ...prev,
        patient_cpf: formatCpf(digits),
        patient_name: resolvedName,
        // Nascimento já informado pelo atendente; não sobrescrever com a edge.
        patient_birth_date: prev.patient_birth_date.trim() || birthKey || '',
      }))
      void loadContactsPrefill(digits)
        .then(({ phones, emails }) => {
          setForm((prev) => {
            if (prev.patient_is_insured) return prev
            void Promise.resolve().then(() => {
              setContactPhoneSuggestions(phones)
              setContactEmailSuggestions(emails)
            })
            return {
              ...prev,
              patient_phone: prev.patient_phone.trim()
                ? prev.patient_phone
                : phones[0] || prev.patient_phone,
              client_emails:
                prev.client_emails.length > 0
                  ? prev.client_emails
                  : emails.length > 0
                    ? [emails[0]]
                    : prev.client_emails,
            }
          })
        })
        .catch(() => {
          /* prefill opcional */
        })
    } catch (e) {
      if (lookedUpPatientCpfRef.current !== lookupKey) return
      const personNotFound =
        e instanceof Error && /pessoa não encontrada/i.test(e.message)
      if (personNotFound) {
        // Mantém lookupKey para não repetir a edge; libera nome manual.
        setPatientNameManual(true)
        setForm((prev) => ({
          ...prev,
          patient_cpf: formatCpf(digits),
          patient_name: prev.patient_name.trim() ? prev.patient_name : '',
          patient_birth_date: prev.patient_birth_date.trim() || birthKey || '',
        }))
        setError(null)
        return
      }
      lookedUpPatientCpfRef.current = null
      setPatientNameManual(false)
      setForm((prev) => ({
        ...prev,
        patient_name: '',
      }))
      setError(e instanceof Error ? e.message : 'Não foi possível buscar o CPF do Dependente.')
    } finally {
      if (
        lookedUpPatientCpfRef.current === lookupKey ||
        lookedUpPatientCpfRef.current === null
      ) {
        setLookingUp(null)
      }
    }
  }, [])

  const lookupHolderRef = React.useRef(lookupHolderEligibility)
  lookupHolderRef.current = lookupHolderEligibility
  const lookupPatientRef = React.useRef(lookupPatientPessoa)
  lookupPatientRef.current = lookupPatientPessoa

  React.useEffect(() => {
    if (!open) {
      setCreatedSummary(null)
      setAvailabilityModalOpen(false)
      lookedUpHolderCpfRef.current = null
      lookedUpPatientCpfRef.current = null
      return
    }
    lookedUpHolderCpfRef.current = null
    lookedUpPatientCpfRef.current = null
    setCreatedSummary(null)
    setAvailabilityModalOpen(false)
    setError(null)
    setWarning(null)
    setPatientNameManual(false)
    setSlotsError(null)
    setSlotFilterWarning(null)
    setFreeProfessionalIds(null)
    slotAvailabilityCacheRef.current = new Map()
    setEligibleSpecializationIds(null)
    setEligibleEspecializacoes(null)
    setCatalogSpecIdsForAssistance(null)
    setSelectedCoverageScope(null)
    setProfessionals([])
    setContactPhoneSuggestions([])
    setContactEmailSuggestions([])
    setCancelReasonId('')
    setCancellationChannel('')
    setCancelReasons([])
    setSavingPreBooking(false)
    void supportCenterParametersService.getSchedulingLimits().then(setSchedulingLimits)
    if (mode === 'edit' && initial) {
      setPreBookingId(null)
      setLoadingCancelReasons(true)
      void supportCenterCancelReasonsService
        .listByKind('atendimento')
        .then((list) => {
          setCancelReasons(list)
          if (list.length === 0) {
            setError(
              'Nenhum motivo cadastrado. Cadastre em Parâmetros > Motivos de cancelamento > Atendimento.'
            )
          }
        })
        .catch((e) => {
          setCancelReasons([])
          setError(
            e instanceof Error ? e.message : 'Não foi possível carregar os motivos de cancelamento.'
          )
        })
        .finally(() => setLoadingCancelReasons(false))
      const next = appointmentToForm(initial)
      setForm(next)
      const holderDigits = onlyDigits(next.holder_cpf)
      if (holderDigits.length === 11) {
        void lookupHolderRef.current(holderDigits)
      }
      if (
        !next.patient_is_insured &&
        next.patient_birth_date.trim() &&
        onlyDigits(next.patient_cpf).length === 11
      ) {
        void lookupPatientRef.current(
          next.patient_cpf,
          next.patient_birth_date.trim().slice(0, 10)
        )
      }
    } else if (mode === 'create' && initial?.status === 'pre_agendado') {
      // Completar/confirmar pré existente: hidrata o form e promove no create.
      setLoadingCancelReasons(false)
      setPreBookingId(initial.id)
      const next = appointmentToForm(initial)
      setForm(next)
      const holderDigits = onlyDigits(next.holder_cpf)
      if (holderDigits.length === 11) {
        void lookupHolderRef.current(holderDigits)
      }
      if (
        !next.patient_is_insured &&
        next.patient_birth_date.trim() &&
        onlyDigits(next.patient_cpf).length === 11
      ) {
        void lookupPatientRef.current(
          next.patient_cpf,
          next.patient_birth_date.trim().slice(0, 10)
        )
      }
    } else {
      setLoadingCancelReasons(false)
      setPreBookingId(null)
      setForm({
        ...EMPTY_APPOINTMENT_FORM,
        specialization_id: '',
        professional_user_id: '',
        scheduled_at: initialScheduledAt ? toDatetimeLocal(initialScheduledAt) : '',
        patient_is_insured: true,
      })
    }
  }, [open, mode, initial, initialScheduledAt, currentUserId])

  // Com horário travado: quais profissionais estão livres naquele slot.
  React.useEffect(() => {
    if (!open || !slotLocked || !selectedDate || !selectedHour || !eligibleEspecializacoes) {
      if (!slotLocked) {
        setFreeProfessionalIds(null)
        setLoadingSlotFilter(false)
        setSlotFilterWarning(null)
      }
      return
    }

    let cancelled = false
    void (async () => {
      setLoadingSlotFilter(true)
      setSlotFilterWarning(null)
      try {
        const candidates = new Map<string, { id: string; email: string }>()
        for (const spec of eligibleEspecializacoes) {
          if (isSpecialistScope && !userSpecSet.has(spec.id)) continue
          for (const pro of spec.profissionais ?? []) {
            if (!pro.id) continue
            if (lockProfessionalToSelf && currentUserId && pro.id !== currentUserId) continue
            candidates.set(pro.id, { id: pro.id, email: pro.email?.trim() || '' })
          }
        }

        const now = new Date()
        const free = new Set<string>()
        await Promise.all(
          [...candidates.values()].map(async (pro) => {
            const cacheKey = `${pro.id}|${selectedDate}`
            let slots = slotAvailabilityCacheRef.current.get(cacheKey)
            if (!slots) {
              slots = await supportCenterAppointmentsService.listAvailableSlots(
                { userId: pro.id, email: pro.email },
                selectedDate
              )
              slotAvailabilityCacheRef.current.set(cacheKey, slots)
            }
            const match = slots.some((s) => {
              const hour = (s.horario || '').slice(0, 5)
              return (
                (hour === selectedHour || hour.slice(0, 2) === selectedHour.slice(0, 2)) &&
                isSlotBookable(s.scheduled_at, schedulingLimits, now)
              )
            })
            if (match) free.add(pro.id)
          })
        )

        if (cancelled) return
        setFreeProfessionalIds(free)
        if (free.size === 0) {
          setSlotFilterWarning(
            lockProfessionalToSelf
              ? 'Você não está livre neste horário. Escolha outro slot na agenda.'
              : 'Ninguém livre neste horário. Escolha outro slot na agenda.'
          )
        }
      } catch (e) {
        if (!cancelled) {
          setFreeProfessionalIds(new Set())
          setSlotFilterWarning(
            e instanceof Error
              ? e.message
              : 'Não foi possível verificar disponibilidade neste horário.'
          )
        }
      } finally {
        if (!cancelled) setLoadingSlotFilter(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    open,
    slotLocked,
    selectedDate,
    selectedHour,
    eligibleEspecializacoes,
    isSpecialistScope,
    lockProfessionalToSelf,
    userSpecSet,
    currentUserId,
    schedulingLimits,
  ])

  // Catálogo — especialidades e cobertura da assistência escolhida.
  React.useEffect(() => {
    const code = form.assistance_code.trim()
    if (!open || !code) {
      setCatalogSpecIdsForAssistance(null)
      setSelectedCoverageScope(null)
      return
    }
    let cancelled = false
    setCatalogSpecIdsForAssistance(null)
    setSelectedCoverageScope(null)
    void Promise.all([
      supportCenterAssistancesService.listSpecializationIdsByAssistanceCode(code),
      supportCenterAssistancesService.getActiveByCode(code),
    ])
      .then(([ids, assistance]) => {
        if (cancelled) return
        setCatalogSpecIdsForAssistance(ids)
        setSelectedCoverageScope(assistance?.coverage_scope ?? null)
      })
      .catch(() => {
        if (cancelled) return
        setCatalogSpecIdsForAssistance([])
        setSelectedCoverageScope(null)
      })
    return () => {
      cancelled = true
    }
  }, [open, form.assistance_code])

  // Somente titular: trava paciente = segurado.
  React.useEffect(() => {
    if (!open || selectedCoverageScope !== 'titular') return
    if (form.patient_is_insured) return
    setForm((prev) => ({
      ...prev,
      patient_is_insured: true,
      patient_cpf: prev.holder_cpf,
      patient_name: prev.holder_name,
      patient_birth_date: prev.holder_birth_date,
      relationship_to_holder: '',
      contact_owner: 'holder',
      client_emails: [],
      patient_phone: '',
    }))
    applyContactPrefill(form.holder_cpf, true)
  }, [
    open,
    selectedCoverageScope,
    form.patient_is_insured,
    form.holder_cpf,
    applyContactPrefill,
  ])

  // Auto-seleciona especialidade quando só há uma opção para a assistência.
  const selectableSpecIdsKey = selectableSpecializations.map((spec) => spec.id).join('|')
  React.useEffect(() => {
    if (!open || !form.assistance_code.trim()) return
    if (catalogSpecIdsForAssistance === null) return
    if (loadingSlotFilter) return
    const onlySpec =
      selectableSpecializations.length === 1 ? selectableSpecializations[0].id : ''
    setForm((prev) => {
      const stillValid = Boolean(
        prev.specialization_id &&
          selectableSpecializations.some((spec) => spec.id === prev.specialization_id)
      )
      if (stillValid) return prev
      if (onlySpec === prev.specialization_id) return prev
      // Completar pré: não zerar profissional/hora do hold enquanto o catálogo estabiliza.
      if (confirmingPreBooking && prev.professional_user_id && hourPart(prev.scheduled_at)) {
        return onlySpec ? { ...prev, specialization_id: onlySpec } : prev
      }
      return {
        ...prev,
        specialization_id: onlySpec,
        professional_user_id: '',
        scheduled_at: preserveScheduledAt(prev.scheduled_at, slotLocked),
      }
    })
    // selectableSpecializations via selectableSpecIdsKey
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    form.assistance_code,
    catalogSpecIdsForAssistance,
    selectableSpecIdsKey,
    loadingSlotFilter,
    slotLocked,
    confirmingPreBooking,
  ])

  // Pessoas: elegibilidade (Ativo) ou listAttendantsForAgenda (Falecido / sem profissionais).
  React.useEffect(() => {
    if (!open || !form.specialization_id || !eligibleEspecializacoes) {
      setProfessionals([])
      return
    }

    let cancelled = false
    const spec = eligibleEspecializacoes.find((item) => item.id === form.specialization_id)
    const fromEleg = (spec?.profissionais ?? [])
      .map((row) => ({
        id: row.id,
        name: row.name.trim() || row.email,
        email: row.email.trim(),
      }))
      .filter((row) => Boolean(row.id && row.email))

    void (async () => {
      let list: SupportCenterAttendantOption[] = fromEleg
      if (list.length === 0) {
        try {
          list = await supportCenterAppointmentsService.listAttendantsForAgenda(
            form.specialization_id
          )
        } catch {
          if (!cancelled) list = []
        }
      }
      if (cancelled) return

      list = [...list].sort((a, b) =>
        a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
      )

      if (slotLocked && freeProfessionalIds) {
        list = list.filter((p) => freeProfessionalIds.has(p.id))
      }

      setProfessionals(list)

      const byId = new Map(list.map((p) => [p.id, p]))
      const byEmail = new Map(list.map((p) => [p.email.trim().toLowerCase(), p]))

      let specialistMissing = false
      if (lockProfessionalToSelf && currentUserId && !byId.has(currentUserId)) {
        specialistMissing = true
        if (slotLocked && freeProfessionalIds && !freeProfessionalIds.has(currentUserId)) {
          setError('Você não está livre neste horário. Escolha outro slot na agenda.')
        }
      }

      setForm((prev) => {
        let nextId = prev.professional_user_id

        if (lockProfessionalToSelf && currentUserId && byId.has(currentUserId)) {
          nextId = currentUserId
        } else if (
          list.length === 1 &&
          (!lockProfessionalToSelf || list[0].id === currentUserId)
        ) {
          nextId = list[0].id
        } else if (lockProfessionalToSelf && currentUserId) {
          nextId = specialistMissing ? '' : currentUserId
        } else {
          if (nextId && !byId.has(nextId)) {
            const initialEmail =
              mode === 'edit' || confirmingPreBooking
                ? (initial?.professional?.email || initial?.internal_emails?.[0] || '')
                    .trim()
                    .toLowerCase()
                : ''
            nextId = (initialEmail && byEmail.get(initialEmail)?.id) || ''
          }
          if (!nextId && currentUserId && byId.has(currentUserId)) {
            nextId = currentUserId
          }
        }

        if (
          confirmingPreBooking &&
          !nextId &&
          initial?.professional_user_id?.trim()
        ) {
          nextId = initial.professional_user_id.trim()
        }

        // Completar pré: se o profissional do hold não veio na lista da elegibilidade,
        // injeta a opção a partir do initial para não perder a seleção.
        if (
          confirmingPreBooking &&
          nextId &&
          !byId.has(nextId) &&
          initial?.professional_user_id === nextId
        ) {
          const fallback: SupportCenterAttendantOption = {
            id: nextId,
            name:
              initial.professional?.name?.trim() ||
              initial.professional?.email?.trim() ||
              nextId,
            email:
              initial.professional?.email?.trim() ||
              initial.internal_emails?.[0]?.trim() ||
              '',
          }
          if (fallback.email) {
            list = [...list, fallback].sort((a, b) =>
              a.name.localeCompare(b.name, 'pt-BR', { sensitivity: 'base' })
            )
            byId.set(fallback.id, fallback)
            setProfessionals(list)
          }
        }

        if (nextId && !byId.has(nextId)) nextId = ''

        const professional = nextId ? byId.get(nextId) : undefined
        const shouldDefaultInternal =
          mode === 'create' && prev.internal_emails.length === 0 && professional?.email
        if (nextId === prev.professional_user_id && !shouldDefaultInternal) return prev
        return {
          ...prev,
          professional_user_id: nextId,
          ...(shouldDefaultInternal
            ? { internal_emails: [String(professional.email).trim().toLowerCase()] }
            : {}),
        }
      })
    })()

    return () => {
      cancelled = true
    }
  }, [
    open,
    form.specialization_id,
    eligibleEspecializacoes,
    currentUserId,
    mode,
    initial,
    lockProfessionalToSelf,
    slotLocked,
    freeProfessionalIds,
    confirmingPreBooking,
  ])

  // Carrega horários livres (hora cheia) para a data + profissional
  React.useEffect(() => {
    if (!open || !form.professional_user_id || !selectedDate) {
      setAvailableHours([])
      setSlotsError(null)
      setWarning(null)
      return
    }

    let cancelled = false
    void (async () => {
      setLoadingSlots(true)
      setSlotsError(null)
      setWarning(null)
      try {
        const cacheKey = `${form.professional_user_id}|${selectedDate}`
        let slots = slotAvailabilityCacheRef.current.get(cacheKey)
        if (!slots) {
          slots = await supportCenterAppointmentsService.listAvailableSlots(
            {
              userId: form.professional_user_id,
              email: selectedProfessionalEmail,
            },
            selectedDate
          )
          slotAvailabilityCacheRef.current.set(cacheKey, slots)
        }

        // Em edição / completar pré, mantém o horário atual mesmo se a API o considerar ocupado
        // (o próprio pré/agendamento ocupa o slot).
        const keepOwnSlot = (mode === 'edit' || confirmingPreBooking) && Boolean(initial)
        const currentHour = keepOwnSlot ? hourPart(toDatetimeLocal(initial!.scheduled_at)) : ''
        const currentScheduled = keepOwnSlot ? toDatetimeLocal(initial!.scheduled_at) : ''
        const sameDay =
          keepOwnSlot && datePart(toDatetimeLocal(initial!.scheduled_at)) === selectedDate

        const now = new Date()
        let nextSlots = slots.filter((s) => isSlotBookable(s.scheduled_at, schedulingLimits, now))
        if (
          sameDay &&
          currentHour &&
          currentScheduled &&
          !nextSlots.some((s) => s.horario === currentHour)
        ) {
          nextSlots = [
            ...nextSlots,
            { horario: currentHour, scheduled_at: fromDatetimeLocal(currentScheduled) },
          ].sort((a, b) => a.horario.localeCompare(b.horario))
        }

        if (cancelled) return
        setAvailableHours(nextSlots)

        setForm((prev) => {
          const hour = hourPart(prev.scheduled_at)
          if (!hour) return prev
          if (
            nextSlots.some(
              (s) => s.horario === hour || s.horario.slice(0, 2) === hour.slice(0, 2)
            )
          ) {
            return prev
          }
          if (slotLocked || confirmingPreBooking) return prev
          // Horário escolhido ficou indisponível → limpa só a hora
          return { ...prev, scheduled_at: selectedDate ? `${selectedDate}T` : '' }
        })

        if (nextSlots.length === 0) {
          setWarning('Não há horários livres neste dia para o profissional.')
        }
      } catch (e) {
        if (!cancelled) {
          setAvailableHours([])
          setSlotsError(
            e instanceof Error ? e.message : 'Não foi possível carregar horários disponíveis.'
          )
        }
      } finally {
        if (!cancelled) setLoadingSlots(false)
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    open,
    selectedProfessionalEmail,
    form.professional_user_id,
    selectedDate,
    mode,
    initial,
    schedulingLimits,
    slotLocked,
    confirmingPreBooking,
  ])

  const setSelectedDate = (dateYmd: string) => {
    if (slotLocked) return
    setForm((prev) => {
      const hour = hourPart(prev.scheduled_at)
      if (!dateYmd) return { ...prev, scheduled_at: '' }
      if (!hour) return { ...prev, scheduled_at: `${dateYmd}T` }
      return { ...prev, scheduled_at: combineDateAndHour(dateYmd, hour) }
    })
  }

  const setSelectedHour = (hourHm: string) => {
    if (slotLocked) return
    setForm((prev) => {
      const date = datePart(prev.scheduled_at)
      if (!date || !hourHm) return { ...prev, scheduled_at: date ? `${date}T` : '' }
      return { ...prev, scheduled_at: combineDateAndHour(date, hourHm) }
    })
  }

  const canOpenAvailability =
    Boolean(form.specialization_id) && professionals.length > 0 && !slotLocked

  const handleAvailabilitySelect = React.useCallback(
    (selection: AgendaAvailabilitySelection) => {
      const professional = professionals.find((p) => p.id === selection.professionalUserId)
      const nextEmail = professional?.email
        ? String(professional.email).trim().toLowerCase()
        : ''
      setForm((prev) => {
        let internal_emails = prev.internal_emails
        if (mode === 'create' && nextEmail) {
          const professionalEmails = new Set(
            professionals
              .map((p) => String(p.email ?? '').trim().toLowerCase())
              .filter(Boolean)
          )
          const teamOnly = prev.internal_emails
            .map((email) => email.trim().toLowerCase())
            .filter((email) => email && !professionalEmails.has(email))
          internal_emails = [nextEmail, ...teamOnly]
        }
        return {
          ...prev,
          professional_user_id: selection.professionalUserId,
          scheduled_at: selection.scheduledAtLocal,
          internal_emails,
        }
      })
    },
    [professionals, mode]
  )
  const handleHolderCpfChange = (value: string) => {
    const formatted = formatCpf(value)
    const digits = onlyDigits(formatted)
    if (digits.length < 11) {
      lookedUpHolderCpfRef.current = null
      setEligibleSpecializationIds(null)
      setEligibleEspecializacoes(null)
      setProfessionals([])
      setContactPhoneSuggestions([])
      setContactEmailSuggestions([])
      setError(null)
    }
    setForm((prev) => ({
      ...prev,
      holder_cpf: formatted,
      ...(prev.patient_is_insured ? { patient_cpf: formatted } : {}),
      ...(digits.length < 11
        ? {
            holder_name: '',
            holder_birth_date: '',
            assistance_code: '',
            specialization_id: '',
            professional_user_id: '',
            client_emails: [],
            ...(prev.patient_is_insured ? { patient_name: '', patient_birth_date: '' } : {}),
          }
        : {}),
    }))
    if (digits.length === 11) {
      void lookupHolderEligibility(digits)
    }
  }

  const handleHolderStatusChange = (status: SupportCenterHolderVitalStatus) => {
    if (status === form.holder_status) return
    lookedUpHolderCpfRef.current = null
    setEligibleSpecializationIds(null)
    setEligibleEspecializacoes(null)
    setProfessionals([])
    setError(null)
    setForm((prev) => ({
      ...prev,
      holder_status: status,
      assistance_code: '',
      specialization_id: '',
      professional_user_id: '',
      scheduled_at: preserveScheduledAt(prev.scheduled_at, slotLocked),
    }))
    holderStatusRef.current = status
    const digits = onlyDigits(form.holder_cpf)
    if (digits.length === 11) {
      void lookupHolderEligibility(digits, status)
    }
  }

  const handlePatientBirthDateChange = (value: string) => {
    lookedUpPatientCpfRef.current = null
    setPatientNameManual(false)
    setForm((prev) => ({
      ...prev,
      patient_birth_date: value,
      ...(value.trim()
        ? {}
        : {
            patient_cpf: '',
            patient_name: '',
            relationship_to_holder: '',
          }),
    }))
    if (!value.trim()) return
    const digits = onlyDigits(form.patient_cpf)
    if (digits.length === 11) {
      void lookupPatientPessoa(digits, value.trim().slice(0, 10))
    }
  }

  const handlePatientCpfChange = (value: string) => {
    const formatted = formatCpf(value)
    const digits = onlyDigits(formatted)
    if (digits.length < 11) {
      lookedUpPatientCpfRef.current = null
      setPatientNameManual(false)
    }
    setForm((prev) => ({
      ...prev,
      patient_cpf: formatted,
      ...(digits.length < 11 ? { patient_name: '' } : {}),
    }))
    if (digits.length === 11 && form.patient_birth_date.trim()) {
      void lookupPatientPessoa(digits, form.patient_birth_date.trim().slice(0, 10))
    }
  }

  const handleCreatePreBooking = async () => {
    if (!canCreatePreBooking) return
    setSavingPreBooking(true)
    setError(null)
    try {
      const holderCpf = onlyDigits(form.holder_cpf)
      if (holderCpf.length !== 11) throw new Error('Informe o CPF do segurado com 11 dígitos.')
      if (!form.holder_name.trim()) throw new Error('Informe o nome do segurado.')
      if (!form.holder_birth_date.trim()) {
        throw new Error('Informe a data de nascimento do segurado.')
      }
      if (!form.assistance_code.trim()) throw new Error('Selecione a assistência.')
      const channel = form.channel
      if (channel !== 'whatsapp' && channel !== 'telefone' && channel !== 'videochamada') {
        throw new Error('Selecione o canal de agendamento (Telefone, WhatsApp ou Videochamada).')
      }
      if (!form.specialization_id) throw new Error('Selecione a especialidade.')
      if (!form.professional_user_id) throw new Error('Selecione o profissional.')
      if (!selectedDate) throw new Error('Informe a data.')
      if (!selectedHour) throw new Error('Selecione um horário disponível.')
      if (!form.scheduled_at || form.scheduled_at.length < 16) {
        throw new Error('Informe data e horário disponíveis.')
      }

      const scheduledIso = fromDatetimeLocal(form.scheduled_at)
      const reason = checkSlotBookable(scheduledIso, schedulingLimits)
      const msg = slotBookableErrorMessage(reason, schedulingLimits)
      if (msg) throw new Error(msg)
      const hourStillFree = availableHours.some(
        (slot) =>
          slot.horario === selectedHour ||
          slot.horario.slice(0, 2) === selectedHour.slice(0, 2)
      )
      const lockedAndConfirmedFree =
        slotLocked && Boolean(freeProfessionalIds?.has(form.professional_user_id))
      if (!hourStillFree && !lockedAndConfirmedFree && !preBookingId) {
        throw new Error('Horário indisponível. Selecione um horário livre da lista.')
      }

      const professional = professionals.find((p) => p.id === form.professional_user_id)
      const professionalEmail = professional?.email?.trim()
      if (!professionalEmail) {
        throw new Error('Este profissional não tem e-mail cadastrado. Não é possível agendar.')
      }

      let internalEmails = [...form.internal_emails]
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean)
      if (internalEmails.length === 0) {
        internalEmails = [professionalEmail]
      }

      const patientCpf = form.patient_is_insured
        ? holderCpf
        : onlyDigits(form.patient_cpf) || holderCpf

      const saved = await supportCenterAppointmentsService.createPreBooking({
        holder_cpf: holderCpf,
        holder_name: form.holder_name.trim() || null,
        holder_birth_date: form.holder_birth_date.trim().slice(0, 10) || null,
        holder_deceased: form.holder_status === 'falecido',
        patient_cpf: patientCpf,
        patient_name: form.patient_is_insured
          ? form.holder_name.trim() || null
          : form.patient_name.trim() || null,
        patient_birth_date: form.patient_is_insured
          ? form.holder_birth_date.trim().slice(0, 10) || null
          : form.patient_birth_date.trim().slice(0, 10) || null,
        relationship_to_holder: form.patient_is_insured
          ? null
          : form.relationship_to_holder.trim() || null,
        specialization_id: form.specialization_id,
        assistance_code: form.assistance_code.trim(),
        professional_user_id: form.professional_user_id,
        professional_email: professionalEmail,
        scheduled_at: scheduledIso,
        channel,
        internal_emails: internalEmails,
        elegibilidadeEspecialidades: eligibleEspecializacoes ?? [],
      })

      if (!saved?.id) {
        throw new Error('Não foi possível salvar o pré-agendamento. Tente novamente.')
      }

      setPreBookingId(saved.id)
      onSaved(saved)

      const coverage = holderCoverages.find(
        (item) => item.code.trim() === form.assistance_code.trim()
      )
      const specializationName =
        saved.specialization?.name ||
        specializations.find((spec) => spec.id === form.specialization_id)?.name ||
        ''
      const professionalName =
        saved.professional?.name ||
        professionals.find((person) => person.id === form.professional_user_id)?.name ||
        ''
      const assistanceLabel = saved.assistance
        ? `${saved.assistance.name} (${saved.assistance.code})`
        : coverage
          ? `${coverage.name} (${coverage.code})`
          : form.assistance_code.trim()
      const patientIsHolder = form.patient_is_insured
      const rows = buildAppointmentSummaryRows({
        scheduledAt: saved.scheduled_at,
        channel: saved.channel || form.channel,
        assistanceLabel,
        specializationName,
        professionalName,
        holderName: saved.holder_people?.name || form.holder_name.trim(),
        holderCpf: saved.holder_cpf || form.holder_cpf,
        patientName: saved.patient_people?.name || form.patient_name.trim(),
        patientCpf: saved.patient_cpf || patientCpf,
        patientIsHolder,
        relationship: form.relationship_to_holder,
      })
      setCreatedSummary({ rows, kind: 'prebooking' })
    } catch (e) {
      const maybe = e as { message?: string }
      setError(
        (e instanceof Error ? e.message : maybe?.message)?.trim() ||
          'Erro ao salvar o pré-agendamento.'
      )
    } finally {
      setSavingPreBooking(false)
    }
  }

  const handleSubmit = async () => {
    if (!canSubmit || saving || savingPreBooking || !canSchedule) return
    setSaving(true)
    setError(null)
    try {
      const holderCpf = onlyDigits(form.holder_cpf)
      const patientCpf = form.patient_is_insured ? holderCpf : onlyDigits(form.patient_cpf)
      if (holderCpf.length !== 11) throw new Error('Informe o CPF do segurado com 11 dígitos.')
      if (patientCpf.length !== 11) throw new Error('Informe o CPF do Dependente com 11 dígitos.')
      if (!form.patient_is_insured && !form.patient_birth_date.trim()) {
        throw new Error('Informe a data de nascimento do Dependente.')
      }
      if (!form.patient_is_insured && !form.patient_name.trim()) {
        throw new Error('Informe o nome do Dependente.')
      }
      if (!form.patient_is_insured && !form.relationship_to_holder.trim()) {
        throw new Error(
          'Informe o parentesco do Dependente com o segurado (obrigatório quando o Dependente é diferente do titular).'
        )
      }
      if (
        !form.patient_is_insured &&
        !(RELATIONSHIP_TO_HOLDER_OPTIONS as readonly string[]).includes(
          form.relationship_to_holder.trim()
        )
      ) {
        throw new Error('Selecione o parentesco: Cônjuge ou Filho.')
      }
      if (!form.assistance_code.trim()) {
        throw new Error('Selecione a assistência.')
      }
      const channel = form.channel
      if (channel !== 'whatsapp' && channel !== 'telefone' && channel !== 'videochamada') {
        throw new Error('Selecione o canal de agendamento (Telefone, WhatsApp ou Videochamada).')
      }
      if (mode === 'edit') {
        if (!cancelReasonId.trim()) {
          throw new Error('Selecione o motivo do cancelamento do agendamento anterior.')
        }
        if (
          cancellationChannel !== 'whatsapp' &&
          cancellationChannel !== 'telefone' &&
          cancellationChannel !== 'videochamada'
        ) {
          throw new Error(
            'Selecione o canal do cancelamento do agendamento anterior (Telefone, WhatsApp ou Videochamada).'
          )
        }
      }
      if (selectedCoverageScope === 'titular' && !form.patient_is_insured) {
        throw new Error('Esta assistência cobre somente o titular.')
      }
      if (!hasEligibleSpecs) {
        throw new Error(NO_ASSISTANCE_MESSAGE)
      }
      if (!form.specialization_id) throw new Error('Selecione a especialidade.')
      if (!form.professional_user_id) throw new Error('Selecione o profissional.')
      if (form.holder_status === 'ativo') {
        const coberturaElegivel = pickCoverageForAppointment(
          eligibleEspecializacoes,
          form.specialization_id,
          form.assistance_code
        )
        if (
          coberturaElegivel?.codApolice == null ||
          coberturaElegivel?.codConvenio == null ||
          coberturaElegivel?.codCorretor == null ||
          coberturaElegivel?.codCliente == null
        ) {
          throw new Error(
            'Não foi possível obter apólice e convênio da elegibilidade para esta especialidade. Valide o CPF do segurado novamente.'
          )
        }
      }
      if (!selectedDate) throw new Error('Informe a data.')
      if (!selectedHour) throw new Error('Selecione um horário disponível.')
      if (!form.scheduled_at || form.scheduled_at.length < 16) {
        throw new Error('Informe data e horário disponíveis.')
      }

      const scheduledIso = fromDatetimeLocal(form.scheduled_at)
      const keepingSameSlot =
        mode === 'edit' &&
        initial &&
        toDatetimeLocal(initial.scheduled_at) === toDatetimeLocal(form.scheduled_at)
      // Pré próprio já ocupa o horário na disponibilidade — não exigir slot "livre".
      const holdingOwnPreBooking = Boolean(preBookingId)
      if (!keepingSameSlot) {
        const reason = checkSlotBookable(scheduledIso, schedulingLimits)
        const msg = slotBookableErrorMessage(reason, schedulingLimits)
        if (msg) throw new Error(msg)
        const hourStillFree = availableHours.some(
          (slot) =>
            slot.horario === selectedHour ||
            slot.horario.slice(0, 2) === selectedHour.slice(0, 2)
        )
        const lockedAndConfirmedFree =
          slotLocked &&
          Boolean(freeProfessionalIds?.has(form.professional_user_id))
        if (!hourStillFree && !lockedAndConfirmedFree && !holdingOwnPreBooking) {
          throw new Error('Horário indisponível. Selecione um horário livre da lista.')
        }
      }
      // Inclui e-mail digitado e ainda não confirmado com "Adicionar e-mail".
      const flushedClientEmails =
        clientEmailsInputRef.current?.flushPending() ?? form.client_emails
      const patientEmails = [
        ...new Set(
          flushedClientEmails.map((email) => email.trim().toLowerCase()).filter(Boolean)
        ),
      ]
      if (patientEmails.length === 0) {
        throw new Error(
          `Informe o e-mail do ${form.patient_is_insured ? 'segurado' : 'Dependente'}.`
        )
      }
      const patientPhone = form.patient_phone.trim()
      if (!patientPhone) {
        throw new Error('Informe o telefone para contato.')
      }
      if (onlyDigits(patientPhone).length < 10) {
        throw new Error('Informe um telefone para contato válido (DDD + número).')
      }

      const professional = professionals.find((p) => p.id === form.professional_user_id)
      const professionalEmail = professional?.email?.trim()
      if (!professionalEmail) {
        throw new Error('Este profissional não tem e-mail cadastrado. Não é possível agendar.')
      }

      let internalEmails = [...form.internal_emails]
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean)
      if (internalEmails.length === 0) {
        internalEmails = [professionalEmail]
      }

      if (form.patient_is_insured && !form.holder_name.trim()) {
        throw new Error('Informe o nome do segurado.')
      }
      if (form.patient_is_insured && !form.holder_birth_date.trim()) {
        throw new Error('Informe a data de nascimento do segurado.')
      }

      const payloadBase: SupportCenterAppointmentCreateInput = {
        holder_cpf: holderCpf,
        holder_name: form.holder_name.trim() || `CPF ${holderCpf}`,
        holder_birth_date: form.holder_birth_date || null,
        patient_cpf: patientCpf,
        patient_name: form.patient_is_insured
          ? form.holder_name.trim()
          : form.patient_name.trim(),
        patient_birth_date: form.patient_is_insured
          ? form.holder_birth_date || null
          : form.patient_birth_date || null,
        patient_is_insured: form.patient_is_insured,
        relationship_to_holder: form.relationship_to_holder.trim() || null,
        internal_emails: internalEmails,
        patient_emails: patientEmails,
        patient_phone: patientPhone,
        contact_owner: form.patient_is_insured ? 'holder' : 'patient',
        channel,
        specialization_id: form.specialization_id,
        assistance_code: form.assistance_code.trim(),
        professional_email: professionalEmail,
        professional_user_id: form.professional_user_id,
        scheduled_at: scheduledIso,
        elegibilidadeEspecialidades: eligibleEspecializacoes ?? [],
        holder_deceased: form.holder_status === 'falecido',
        ...(preBookingId ? { pre_booking_id: preBookingId } : {}),
      }

      const saved =
        mode === 'edit' && initial
          ? await supportCenterAppointmentsService.update(
              { id: initial.id, ...payloadBase },
              currentUserId || '',
              cancelReasonId,
              cancellationChannel as SupportCenterAppointmentCancelChannel
            )
          : await supportCenterAppointmentsService.create(payloadBase, currentUserId)

      if (!saved?.id) {
        throw new Error('Não foi possível confirmar o agendamento. Tente novamente.')
      }

      onSaved(saved)

      const coverage = holderCoverages.find(
        (item) => item.code.trim() === form.assistance_code.trim()
      )
      const specializationName =
        saved.specialization?.name ||
        specializations.find((spec) => spec.id === form.specialization_id)?.name ||
        ''
      const professionalName =
        saved.professional?.name ||
        professionals.find((person) => person.id === form.professional_user_id)?.name ||
        ''
      const assistanceLabel = saved.assistance
        ? `${saved.assistance.name} (${saved.assistance.code})`
        : coverage
          ? `${coverage.name} (${coverage.code})`
          : form.assistance_code.trim()
      const patientIsHolder = form.patient_is_insured
      const rows = buildAppointmentSummaryRows({
        scheduledAt: saved.scheduled_at,
        channel: saved.channel || form.channel,
        assistanceLabel,
        specializationName,
        professionalName,
        holderName: saved.holder_people?.name || form.holder_name.trim(),
        holderCpf: saved.holder_cpf || form.holder_cpf,
        patientName: saved.patient_people?.name || form.patient_name.trim(),
        patientCpf: saved.patient_cpf || form.patient_cpf,
        patientIsHolder,
        relationship: form.relationship_to_holder,
        patientPhone: saved.patient_phone || form.patient_phone.trim(),
        patientEmail:
          (saved.client_emails?.length ? saved.client_emails : patientEmails).join(', ') || '',
        meetingLink: saved.call_link_short || saved.call_link || '',
      })

      if (mode === 'edit' && initial) {
        const cancelledIsHolder =
          onlyDigits(initial.holder_cpf) === onlyDigits(initial.patient_cpf)
        const cancelledAssistance = initial.assistance
          ? `${initial.assistance.name} (${initial.assistance.code})`
          : ''
        const cancelReasonName =
          cancelReasons.find((reason) => reason.id === cancelReasonId)?.name || ''
        const cancelledRows = buildAppointmentSummaryRows({
          scheduledAt: initial.scheduled_at,
          channel: initial.channel,
          assistanceLabel: cancelledAssistance,
          specializationName: initial.specialization?.name || '',
          professionalName: initial.professional?.name || '',
          holderName: initial.holder_people?.name || '',
          holderCpf: initial.holder_cpf,
          patientName: initial.patient_people?.name || '',
          patientCpf: initial.patient_cpf,
          patientIsHolder: cancelledIsHolder,
          relationship: initial.relationship_to_holder || '',
          patientPhone: initial.patient_phone || '',
          patientEmail: (initial.client_emails ?? []).join(', ') || '',
          meetingLink: initial.call_link_short || initial.call_link || '',
        })
        cancelledRows.push(
          {
            label: 'Canal do cancelamento',
            value: appointmentChannelLabel(cancellationChannel),
          },
          { label: 'Motivo do cancelamento', value: cancelReasonName, wide: true }
        )
        setCreatedSummary({ rows, cancelledRows, kind: 'appointment' })
        return
      }

      setCreatedSummary({ rows, kind: 'appointment' })
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : mode === 'edit'
            ? 'Não foi possível reagendar. Tente novamente.'
            : 'Não foi possível agendar. Tente novamente.'
      )
    } finally {
      setSaving(false)
    }
  }

  const attendedLabel = form.patient_is_insured ? 'segurado' : 'Dependente'
  const attendedLabelTitle = form.patient_is_insured ? 'Segurado' : 'Dependente'

  if (!open) return null

  return (
    <>
    {createPortal(
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4">
      <div className="relative flex max-h-[96vh] w-full max-w-3xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-900">
        {(saving || savingPreBooking) && !createdSummary ? (
          <div
            className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-white/90 dark:bg-gray-900/90"
            role="status"
            aria-live="polite"
          >
            <LoadingSpinner size="lg" />
            <Text className="text-gray-800 dark:text-gray-100">
              {savingPreBooking
                ? 'Salvando o pré-agendamento, aguarde...'
                : mode === 'edit'
                  ? 'Reagendando, aguarde...'
                  : 'Criando o agendamento, aguarde...'}
            </Text>
          </div>
        ) : null}
        <div className="flex items-center justify-between gap-4 px-6 py-4 border-b border-gray-200 dark:border-gray-700 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            {createdSummary ? (
              <CheckCircleIcon
                className="h-7 w-7 shrink-0 text-emerald-600 dark:text-emerald-400"
                aria-hidden="true"
              />
            ) : null}
            <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
              {createdSummary
                ? createdSummary.cancelledRows
                  ? 'Reagendamento realizado com sucesso'
                  : createdSummary.kind === 'prebooking'
                    ? 'Pré-agendamento realizado com sucesso'
                    : 'Agendamento realizado com sucesso'
                : mode === 'edit'
                  ? 'Reagendar'
                  : 'Novo agendamento'}
            </Heading>
          </div>
          {createdSummary ? null : (
            <Button variant="ghost" onClick={onClose} disabled={saving || savingPreBooking}>
              Fechar
            </Button>
          )}
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-6 py-5">
          {createdSummary ? (
            <div className="space-y-5">
              {createdSummary.cancelledRows ? (
                <section className="space-y-2">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Consulta cancelada
                  </h3>
                  <AppointmentCreatedSummary rows={createdSummary.cancelledRows} />
                </section>
              ) : null}
              <section className="space-y-2">
                {createdSummary.cancelledRows ? (
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Nova consulta
                  </h3>
                ) : null}
                <AppointmentCreatedSummary rows={createdSummary.rows} />
                {createdSummary.kind === 'prebooking' ? (
                  <Text size="sm" color="muted">
                    Horário reservado. Para confirmar o agendamento, abra o pré na agenda e
                    complete o contato (telefone e e-mail).
                  </Text>
                ) : null}
              </section>
            </div>
          ) : null}

          {!createdSummary ? (
          <>
          <AppointmentFormSection
            step={1}
            title="Segurado"
            description={
              form.holder_status === 'falecido'
                ? 'Titular do Seguro falecido. Informe o CPF para buscar o cadastro (sem elegibilidade PASI).'
                : 'Titular do Seguro. Informe o CPF para validar elegibilidade.'
            }
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-[minmax(0,16rem)_1fr] gap-3 items-start">
                <AppointmentFormField
                  label="CPF"
                  required
                  hint={
                    lookingUp === 'holder' ? (
                      <div className="mt-1.5 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                        <LoadingSpinner size="sm" className="justify-start" />
                        <span>Buscando dados do segurado...</span>
                      </div>
                    ) : null
                  }
                >
                  <input
                    className={appointmentFormInputClass}
                    value={form.holder_cpf}
                    onChange={(e) => handleHolderCpfChange(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key !== 'Tab' || e.shiftKey) return
                      e.preventDefault()
                      const next = step2FirstControlRef.current
                      if (next && !next.disabled) {
                        next.focus()
                        return
                      }
                      pendingStep2FocusRef.current = true
                    }}
                    onBlur={() => {
                      if (onlyDigits(form.holder_cpf).length === 11) {
                        void lookupHolderEligibility(form.holder_cpf)
                      }
                    }}
                    inputMode="numeric"
                    autoComplete="off"
                    autoFocus={mode === 'create'}
                    placeholder="000.000.000-00"
                    readOnly={lookingUp === 'holder'}
                  />
                </AppointmentFormField>
                <AppointmentFormField label="Situação" required>
                  <div className="grid grid-cols-2 gap-2 max-w-sm">
                    {(
                      [
                        { value: 'ativo' as const, label: 'Ativo' },
                        { value: 'falecido' as const, label: 'Falecido' },
                      ] as const
                    ).map((option) => {
                      const selected = form.holder_status === option.value
                      const isAtivo = option.value === 'ativo'
                      return (
                        <button
                          key={option.value}
                          type="button"
                          disabled={lookingUp === 'holder'}
                          onClick={() => handleHolderStatusChange(option.value)}
                          className={[
                            'rounded-lg border px-3 py-2.5 text-left text-sm transition-colors',
                            selected && isAtivo
                              ? 'border-emerald-600 bg-emerald-50 text-emerald-900 dark:border-emerald-400 dark:bg-emerald-900/30 dark:text-emerald-100'
                              : '',
                            selected && !isAtivo
                              ? 'border-red-600 bg-red-50 text-red-900 dark:border-red-400 dark:bg-red-900/30 dark:text-red-100'
                              : '',
                            !selected
                              ? isAtivo
                                ? 'border-emerald-200 bg-white text-emerald-800 hover:bg-emerald-50 dark:border-emerald-800 dark:bg-gray-800 dark:text-emerald-200 dark:hover:bg-emerald-900/20'
                                : 'border-red-200 bg-white text-red-800 hover:bg-red-50 dark:border-red-800 dark:bg-gray-800 dark:text-red-200 dark:hover:bg-red-900/20'
                              : '',
                            lookingUp === 'holder' ? 'opacity-60 cursor-not-allowed' : '',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          aria-pressed={selected}
                        >
                          <span className="font-medium">{option.label}</span>
                        </button>
                      )
                    })}
                  </div>
                </AppointmentFormField>
              </div>
              <AppointmentFormField label="Nome" required>
                <input
                  className={appointmentFormInputClass}
                  value={form.holder_name}
                  disabled={lookingUp === 'holder'}
                  tabIndex={-1}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      holder_name: e.target.value,
                      ...(prev.patient_is_insured ? { patient_name: e.target.value } : {}),
                    }))
                  }
                />
              </AppointmentFormField>
              <AppointmentFormField label="Nascimento">
                <input
                  type="date"
                  className={appointmentFormInputClass}
                  value={form.holder_birth_date}
                  disabled={lookingUp === 'holder'}
                  tabIndex={-1}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      holder_birth_date: e.target.value,
                      ...(prev.patient_is_insured ? { patient_birth_date: e.target.value } : {}),
                    }))
                  }
                />
              </AppointmentFormField>
            </div>
          </AppointmentFormSection>

          <AppointmentFormSection
            step={2}
            title="Assistência"
            description="Escolha a cobertura elegível do segurado para este agendamento."
          >
            {!showAssistanceStep ? (
              <Text size="sm" color="muted">
                Informe o CPF do segurado no passo 1 para listar as assistências.
              </Text>
            ) : holderCoverages.length === 0 ? (
              <Text size="sm" color="muted">
                {isSpecialistScope
                  ? 'Nenhuma assistência elegível vinculada às suas especialidades para este segurado.'
                  : 'Nenhuma cobertura retornada para este segurado.'}
              </Text>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {holderCoverages.map((cobertura, index) => {
                  const selected = form.assistance_code.trim() === cobertura.code.trim()
                  return (
                    <button
                      key={cobertura.code}
                      type="button"
                      ref={index === 0 ? step2FirstControlRef : undefined}
                      onClick={() => {
                        setForm((prev) => {
                          if (prev.assistance_code.trim() === cobertura.code.trim()) return prev
                          return {
                            ...prev,
                            assistance_code: cobertura.code,
                            specialization_id: '',
                            professional_user_id: '',
                            scheduled_at: preserveScheduledAt(prev.scheduled_at, slotLocked),
                          }
                        })
                        setError(null)
                      }}
                      className={[
                        'rounded-lg border px-3 py-2.5 text-left text-sm transition-colors',
                        selected
                          ? 'border-primary-500 bg-primary-50 text-primary-900 dark:bg-primary-900/30 dark:text-primary-100 dark:border-primary-400'
                          : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200',
                      ].join(' ')}
                    >
                      <span className="font-medium">{cobertura.name}</span>
                      <span className="mt-0.5 block text-xs opacity-80">
                        Código {cobertura.code}
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
          </AppointmentFormSection>

          <AppointmentFormSection
            step={3}
            title={`${attendedLabelTitle} e contato`}
            description="Quem será atendido e como entrar em contato."
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <button
                type="button"
                disabled={lookingUp === 'holder' || !form.assistance_code.trim()}
                onClick={() => {
                  setForm((prev) => ({
                    ...prev,
                    patient_is_insured: true,
                    patient_cpf: prev.holder_cpf,
                    patient_name: prev.holder_name,
                    patient_birth_date: prev.holder_birth_date,
                    relationship_to_holder: '',
                    contact_owner: 'holder',
                    client_emails: [],
                    patient_phone: '',
                  }))
                  applyContactPrefill(form.holder_cpf, true)
                }}
                className={[
                  'rounded-lg border px-3 py-2.5 text-left text-sm transition-colors',
                  form.patient_is_insured
                    ? 'border-primary-500 bg-primary-50 text-primary-900 dark:bg-primary-900/30 dark:text-primary-100 dark:border-primary-400'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200',
                  !form.assistance_code.trim() ? 'opacity-60 cursor-not-allowed' : '',
                ].join(' ')}
              >
                <span className="font-medium">O próprio segurado</span>
                <span className="mt-0.5 block text-xs opacity-80">Segurado é o titular do seguro</span>
              </button>
              <button
                type="button"
                disabled={
                  lookingUp === 'holder' ||
                  !form.assistance_code.trim() ||
                  selectedCoverageScope === 'titular'
                }
                title={
                  selectedCoverageScope === 'titular'
                    ? 'Assistência somente titular'
                    : undefined
                }
                onClick={() => {
                  if (selectedCoverageScope === 'titular') return
                  lookedUpPatientCpfRef.current = null
                  setPatientNameManual(false)
                  setForm((prev) => ({
                    ...prev,
                    patient_is_insured: false,
                    patient_cpf: '',
                    patient_name: '',
                    patient_birth_date: '',
                    relationship_to_holder: '',
                    contact_owner: 'patient',
                    client_emails: [],
                    patient_phone: '',
                  }))
                  setContactPhoneSuggestions([])
                  setContactEmailSuggestions([])
                }}
                className={[
                  'rounded-lg border px-3 py-2.5 text-left text-sm transition-colors',
                  !form.patient_is_insured
                    ? 'border-primary-500 bg-primary-50 text-primary-900 dark:bg-primary-900/30 dark:text-primary-100 dark:border-primary-400'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200',
                  !form.assistance_code.trim() || selectedCoverageScope === 'titular'
                    ? 'opacity-60 cursor-not-allowed'
                    : '',
                ].join(' ')}
              >
                <span className="font-medium">Dependentes</span>
                <span className="mt-0.5 block text-xs opacity-80">
                  Cônjuge/Companheiro(a) ou Filho
                </span>
              </button>
            </div>
            {!form.assistance_code.trim() ? (
              <Text size="xs" color="muted" className="mt-1">
                Selecione a assistência no passo 2.
              </Text>
            ) : selectedCoverageScope === 'titular' ? (
              <Text size="xs" color="muted" className="mt-1">
                Assistência somente titular.
              </Text>
            ) : null}

            {form.patient_is_insured ? null : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                <AppointmentFormField label="Data de Nascimento do Dependente" required>
                  <input
                    type="date"
                    className={appointmentFormInputClass}
                    value={form.patient_birth_date}
                    disabled={lookingUp === 'patient'}
                    onChange={(e) => handlePatientBirthDateChange(e.target.value)}
                    required
                  />
                </AppointmentFormField>
                <AppointmentFormField
                  label="CPF do Dependente"
                  required
                  hint={
                    !form.patient_birth_date.trim() ? (
                      <Text size="xs" color="muted" className="mt-1.5">
                        Informe a data de nascimento para continuar.
                      </Text>
                    ) : lookingUp === 'patient' ? (
                      <div className="mt-1.5 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                        <LoadingSpinner size="sm" className="justify-start" />
                        <span>Buscando dados do Dependente...</span>
                      </div>
                    ) : null
                  }
                >
                  <input
                    className={appointmentFormInputClass}
                    value={form.patient_cpf}
                    onChange={(e) => handlePatientCpfChange(e.target.value)}
                    onBlur={() => {
                      if (
                        form.patient_birth_date.trim() &&
                        onlyDigits(form.patient_cpf).length === 11
                      ) {
                        void lookupPatientPessoa(
                          form.patient_cpf,
                          form.patient_birth_date.trim().slice(0, 10)
                        )
                      }
                    }}
                    inputMode="numeric"
                    autoComplete="off"
                    placeholder="000.000.000-00"
                    disabled={lookingUp === 'patient' || !form.patient_birth_date.trim()}
                  />
                </AppointmentFormField>
                <AppointmentFormField
                  label="Nome do Dependente"
                  required
                  hint={
                    patientNameManual && onlyDigits(form.patient_cpf).length === 11 ? (
                      <Text size="xs" color="muted" className="mt-1.5">
                        Dependente não encontrado na base. Informe o nome manualmente.
                      </Text>
                    ) : null
                  }
                >
                  <input
                    className={appointmentFormInputClass}
                    value={form.patient_name}
                    disabled={lookingUp === 'patient' || !form.patient_birth_date.trim()}
                    onChange={(e) => setForm((prev) => ({ ...prev, patient_name: e.target.value }))}
                    placeholder={patientNameManual ? 'Digite o nome completo' : undefined}
                  />
                </AppointmentFormField>
                <AppointmentFormField label="Parentesco com o segurado" required>
                  <select
                    className={appointmentFormInputClass}
                    value={
                      RELATIONSHIP_TO_HOLDER_OPTIONS.includes(
                        form.relationship_to_holder as (typeof RELATIONSHIP_TO_HOLDER_OPTIONS)[number]
                      )
                        ? form.relationship_to_holder
                        : ''
                    }
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, relationship_to_holder: e.target.value }))
                    }
                    required
                    disabled={!form.patient_birth_date.trim()}
                  >
                    <option value="">Selecione</option>
                    {RELATIONSHIP_TO_HOLDER_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </AppointmentFormField>
              </div>
            )}

            <div className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-3 mt-1">
              <Text size="sm" className="font-medium text-gray-800 dark:text-gray-200">
                Contato do {attendedLabel}
              </Text>

              <ContactPhoneInput
                label={`WhatsApp do ${attendedLabel}`}
                value={form.patient_phone}
                onChange={(value) => setForm((prev) => ({ ...prev, patient_phone: value }))}
                suggestions={contactPhoneSuggestions}
                required
                disabled={
                  lookingUp !== null ||
                  (!form.patient_is_insured && !form.patient_birth_date.trim())
                }
                placeholder="(00) 00000-0000"
                helpText={
                  contactPhoneSuggestions.length > 0
                    ? 'Selecione um cadastrado ou digite um novo.'
                    : `Digite o WhatsApp do ${attendedLabel}.`
                }
              />

              <EmailChipsInput
                ref={clientEmailsInputRef}
                label={`E-mail do ${attendedLabel}`}
                values={form.client_emails}
                onChange={(values) => setForm((prev) => ({ ...prev, client_emails: values }))}
                suggestions={contactEmailSuggestions}
                required
                disabled={
                  lookingUp !== null ||
                  (!form.patient_is_insured && !form.patient_birth_date.trim())
                }
                placeholder="Digite o e-mail"
                helpText={
                  contactEmailSuggestions.length > 0
                    ? 'Selecione um cadastrado ou digite um novo. Use Adicionar e-mail para incluir mais de um.'
                    : `Digite o e-mail do ${attendedLabel}. Se esquecer de clicar em Adicionar, ele ainda será gravado.`
                }
              />
            </div>
          </AppointmentFormSection>

          <AppointmentFormSection
            step={4}
            title="Agenda"
            description={
              slotLocked
                ? 'Horário selecionado na agenda. Escolha a especialidade e o profissional disponíveis neste horário.'
                : 'Especialidade vinculada à assistência, profissional e horário.'
            }
            action={
              !slotLocked ? (
                <button
                  type="button"
                  onClick={() => setAvailabilityModalOpen(true)}
                  disabled={!canOpenAvailability}
                  title={
                    !form.specialization_id
                      ? 'Selecione a especialidade para ver a disponibilidade.'
                      : professionals.length === 0
                        ? 'Nenhum profissional disponível nesta especialidade.'
                        : 'Ver calendário de horários livres'
                  }
                  className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500 bg-white px-2.5 py-1.5 text-xs font-semibold text-emerald-700 shadow-sm transition-colors hover:bg-emerald-50 hover:border-emerald-600 disabled:cursor-not-allowed disabled:opacity-50 dark:border-emerald-400 dark:bg-transparent dark:text-emerald-300 dark:hover:bg-emerald-900/30 dark:hover:border-emerald-300"
                >
                  <CalendarIcon className="h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-300" aria-hidden="true" />
                  <span>Ver disponibilidade</span>
                </button>
              ) : null
            }
          >
            {slotLocked && (
              <div className="rounded-lg border border-primary-300 bg-primary-50 dark:bg-primary-900/20 dark:border-primary-700 px-3 py-2 text-sm text-primary-900 dark:text-primary-100">
                Horário selecionado na agenda:{' '}
                <span className="font-semibold">
                  {selectedDate ? selectedDate.split('-').reverse().join('/') : '—'} às{' '}
                  {selectedHour || '—'}
                </span>
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <AppointmentFormField label="Canal de agendamento" required className="sm:col-span-2 sm:max-w-xs">
                <select
                  className={appointmentFormInputClass}
                  value={form.channel}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      channel: e.target.value as '' | SupportCenterAppointmentFormChannel,
                    }))
                  }
                >
                  <option value="">Selecione...</option>
                  {SUPPORT_CENTER_APPOINTMENT_FORM_CHANNEL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </AppointmentFormField>
              {slotLocked ? (
                <>
                  <AppointmentFormField label="Data" required>
                    <input
                      type="date"
                      className={appointmentFormInputClass}
                      value={selectedDate}
                      disabled
                      readOnly
                    />
                  </AppointmentFormField>
                  <AppointmentFormField label="Horário" required>
                    <input
                      type="text"
                      className={appointmentFormInputClass}
                      value={selectedHour}
                      disabled
                      readOnly
                    />
                  </AppointmentFormField>
                </>
              ) : null}
              <AppointmentFormField
                label="Especialidade"
                required
                hint={
                  !eligibilityReady ? (
                    <Text size="xs" color="muted" className="mt-1">
                      Informe o CPF do segurado no passo 1.
                    </Text>
                  ) : !form.assistance_code.trim() ? (
                    <Text size="xs" color="muted" className="mt-1">
                      Selecione a assistência no passo 2.
                    </Text>
                  ) : catalogSpecIdsForAssistance === null ? (
                    <Text size="xs" color="muted" className="mt-1">
                      Carregando especialidades da assistência...
                    </Text>
                  ) : loadingSlotFilter ? (
                    <Text size="xs" color="muted" className="mt-1">
                      Verificando profissionais livres neste horário...
                    </Text>
                  ) : !hasEligibleSpecs ? (
                    <Text size="xs" color="muted" className="mt-1">
                      Nenhuma especialidade vinculada a esta assistência neste escopo.
                    </Text>
                  ) : slotLocked && selectableSpecializations.length === 0 ? (
                    <Text size="xs" color="muted" className="mt-1">
                      Nenhum profissional livre neste horário.
                    </Text>
                  ) : null
                }
              >
                <select
                  className={appointmentFormInputClass}
                  value={form.specialization_id}
                  disabled={
                    !form.assistance_code.trim() ||
                    catalogSpecIdsForAssistance === null ||
                    !hasEligibleSpecs ||
                    loadingSlotFilter
                  }
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      specialization_id: e.target.value,
                      professional_user_id: '',
                      scheduled_at: preserveScheduledAt(prev.scheduled_at, slotLocked),
                    }))
                  }
                >
                  <option value="">
                    {!eligibilityReady
                      ? 'Aguardando CPF do segurado...'
                      : !form.assistance_code.trim()
                        ? 'Selecione a assistência no passo 2...'
                        : catalogSpecIdsForAssistance === null
                          ? 'Carregando especialidades...'
                          : loadingSlotFilter
                            ? 'Verificando disponibilidade...'
                            : !hasEligibleSpecs
                              ? 'Sem especialidades para esta assistência'
                              : 'Selecione...'}
                  </option>
                  {selectableSpecializations.map((spec) => (
                    <option key={spec.id} value={spec.id}>
                      {spec.name}
                    </option>
                  ))}
                </select>
              </AppointmentFormField>
              <AppointmentFormField label="Profissional" required>
                <select
                  className={appointmentFormInputClass}
                  value={form.professional_user_id}
                  disabled={
                    !form.specialization_id ||
                    !eligibilityReady ||
                    loadingSlotFilter ||
                    (lockProfessionalToSelf && Boolean(form.professional_user_id))
                  }
                  onChange={(e) => {
                    const professionalId = e.target.value
                    const professional = professionals.find((p) => p.id === professionalId)
                    const nextEmail = professional?.email
                      ? String(professional.email).trim().toLowerCase()
                      : ''
                    setForm((prev) => {
                      let internal_emails = prev.internal_emails
                      if (mode === 'create' && nextEmail) {
                        // Troca de profissional: remove e-mails de outros profissionais da lista
                        // e garante o e-mail do selecionado (mantém e-mails de equipe extras).
                        const professionalEmails = new Set(
                          professionals
                            .map((p) => String(p.email ?? '').trim().toLowerCase())
                            .filter(Boolean)
                        )
                        const teamOnly = prev.internal_emails
                          .map((email) => email.trim().toLowerCase())
                          .filter((email) => email && !professionalEmails.has(email))
                        internal_emails = [nextEmail, ...teamOnly]
                      }
                      return {
                        ...prev,
                        professional_user_id: professionalId,
                        scheduled_at: preserveScheduledAt(prev.scheduled_at, slotLocked),
                        internal_emails,
                      }
                    })
                  }}
                >
                  <option value="">
                    {!form.specialization_id
                      ? 'Selecione a especialidade acima...'
                      : professionals.length === 0
                        ? slotLocked
                          ? 'Sem profissionais livres neste horário'
                          : 'Sem profissionais disponíveis'
                        : 'Selecione...'}
                  </option>
                  {professionals.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </AppointmentFormField>
              {!slotLocked ? (
                <>
                  <AppointmentFormField label="Data" required>
                    <input
                      type="date"
                      className={appointmentFormInputClass}
                      value={selectedDate}
                      min={dateBounds.minYmd}
                      max={dateBounds.maxYmd}
                      onChange={(e) => setSelectedDate(e.target.value)}
                    />
                  </AppointmentFormField>
                  <AppointmentFormField label="Horário" required>
                    <select
                      className={appointmentFormInputClass}
                      value={selectedHour}
                      disabled={!selectedDate || !form.professional_user_id || loadingSlots}
                      onChange={(e) => setSelectedHour(e.target.value)}
                    >
                      <option value="">
                        {!form.professional_user_id
                          ? 'Selecione o profissional'
                          : !selectedDate
                            ? 'Selecione a data'
                            : loadingSlots
                              ? 'Carregando...'
                              : availableHours.length === 0
                                ? 'Sem horários livres'
                                : 'Selecione...'}
                      </option>
                      {availableHours.map((slot) => (
                        <option key={slot.horario} value={slot.horario}>
                          {slot.horario}
                        </option>
                      ))}
                    </select>
                  </AppointmentFormField>
                </>
              ) : null}
            </div>
            <div className="pt-1 border-t border-gray-100 dark:border-gray-800">
              <EmailChipsInput
                label="E-mails internos (equipe)"
                values={form.internal_emails}
                onChange={(values) => setForm((prev) => ({ ...prev, internal_emails: values }))}
                placeholder="Incluir e-mail da equipe (Enter)"
                helpText="Para convites e notificações internas. Não é e-mail do segurado."
              />
            </div>
          </AppointmentFormSection>

          {mode === 'edit' ? (
            <AppointmentFormSection
              step={5}
              title="Cancelamento do agendamento anterior"
              description="O reagendamento cancela o horário antigo. Informe o canal e o motivo que o cliente receberá."
            >
              <AppointmentFormField label="Canal do cancelamento" required>
                <select
                  className={appointmentFormInputClass}
                  value={cancellationChannel}
                  disabled={saving || loadingCancelReasons}
                  onChange={(e) =>
                    setCancellationChannel(
                      e.target.value as '' | SupportCenterAppointmentCancelChannel
                    )
                  }
                >
                  <option value="">Selecione...</option>
                  {SUPPORT_CENTER_APPOINTMENT_CANCEL_CHANNEL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </AppointmentFormField>
              <AppointmentFormField
                label="Motivo do cancelamento"
                required
                hint={
                  <p className="mt-1 text-xs font-bold text-red-600 dark:text-red-400">
                    A descrição será a mensagem que o segurado/cliente irá receber
                  </p>
                }
              >
                {loadingCancelReasons ? (
                  <Text size="sm" className="text-gray-600 dark:text-gray-300">
                    Carregando motivos...
                  </Text>
                ) : cancelReasons.length === 0 ? (
                  <Text size="sm" className="text-gray-600 dark:text-gray-300">
                    Nenhum motivo cadastrado. Cadastre em Parâmetros &gt; Motivos de cancelamento.
                  </Text>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {cancelReasons.map((reason) => {
                      const selected = reason.id === cancelReasonId
                      return (
                        <button
                          key={reason.id}
                          type="button"
                          disabled={saving}
                          onClick={() => setCancelReasonId(reason.id)}
                          className={[
                            'w-full text-left rounded-lg border px-3 py-2 transition-colors',
                            selected
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/30'
                              : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/60',
                          ].join(' ')}
                        >
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {reason.name}
                          </div>
                          {reason.description ? (
                            <div className="text-xs text-gray-600 dark:text-gray-300 mt-0.5">
                              {reason.description}
                            </div>
                          ) : null}
                        </button>
                      )
                    })}
                  </div>
                )}
              </AppointmentFormField>
            </AppointmentFormSection>
          ) : null}
          </>
          ) : null}
        </div>

        <div className="flex flex-col gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 shrink-0">
          {!createdSummary && error ? (
            <div className="rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 px-3 py-2 text-sm">
              {error}
            </div>
          ) : null}
          {!createdSummary &&
          !error &&
          (slotsError || slotFilterWarning || warning) ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-700 px-3 py-2 text-sm text-amber-800 dark:text-amber-200">
              {slotsError || slotFilterWarning || warning}
            </div>
          ) : null}
          <div
            className={
              createdSummary || mode === 'edit'
                ? 'flex justify-end gap-3'
                : 'flex flex-wrap items-center justify-between gap-3'
            }
          >
            {createdSummary ? (
              <Button onClick={onClose}>Fechar</Button>
            ) : (
              <>
                {mode === 'create' ? (
                  <Button
                    variant="outline"
                    onClick={() => void handleCreatePreBooking()}
                    disabled={!canCreatePreBooking || Boolean(preBookingId)}
                    title={
                      preBookingId
                        ? 'Pré-agendamento já salvo. Complete o passo 3 e use Criar agendamento.'
                        : undefined
                    }
                  >
                    {savingPreBooking
                      ? 'Salvando pré...'
                      : preBookingId
                        ? 'Pré-agendamento salvo'
                        : 'Criar pré-agendamento'}
                  </Button>
                ) : (
                  <span />
                )}
                <div className="flex gap-3 ml-auto">
                  <Button
                    variant="ghost"
                    onClick={onClose}
                    disabled={saving || savingPreBooking}
                  >
                    Cancelar
                  </Button>
                  <Button onClick={() => void handleSubmit()} disabled={submitDisabled}>
                    {saving
                      ? 'Salvando...'
                      : mode === 'edit'
                        ? 'Reagendar'
                        : preBookingId
                          ? 'Criar agendamento'
                          : 'Criar agendamento'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
    )}
    <AgendaAvailabilityModal
      open={availabilityModalOpen}
      onClose={() => setAvailabilityModalOpen(false)}
      professionals={professionals}
      lockProfessionalToSelf={lockProfessionalToSelf}
      initialProfessionalId={form.professional_user_id}
      minYmd={dateBounds.minYmd}
      maxYmd={dateBounds.maxYmd}
      schedulingLimits={schedulingLimits}
      onSelect={handleAvailabilitySelect}
    />
    </>
  )
}
